import axiosInstance from './axiosInstance';

// ========== AMS Checkpoint Types ==========

export interface ICheckpointMeta {
  executionType: 'manual' | 'visual' | 'hearing';
  documentApplicable: boolean;
  documentTypes: string[];
  designedParameterAvailable: boolean;
  designedParameterCategory?: string;
  designedParameterUom: string;
  actualParameter?: string;
  imagesEnabled: boolean;
  remarksEnabled: boolean;
  applicableAuditTypeIds: string[];
}

export interface AmsCheckpoint {
  _id: string;
  id?: string;
  name: string;
  checkpointType: 'asset' | 'project';
  assetId?: {
    _id: string;
    id?: string;
    name: string;
    code?: string;
  } | string;
  projectTypeId: {
    _id: string;
    id?: string;
    name: string;
    code?: string;
  } | string;
  checkpointMeta: ICheckpointMeta;
  status: 'active' | 'inactive';
  createdBy: {
    _id: string;
    id?: string;
    name: string;
    email: string;
  } | string;
  updatedBy: {
    _id: string;
    id?: string;
    name: string;
    email: string;
  } | string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export interface CreateAmsCheckpointForm {
  name: string;
  checkpointType: 'asset' | 'project';
  assetId?: string; // Required when checkpointType is 'asset'
  projectTypeId: string; // Required
  checkpointMeta: ICheckpointMeta;
  status?: 'active' | 'inactive';
}

export interface UpdateAmsCheckpointForm {
  name?: string;
  checkpointType?: 'asset' | 'project';
  assetId?: string;
  projectTypeId?: string;
  checkpointMeta?: Partial<ICheckpointMeta>;
  status?: 'active' | 'inactive';
}

export interface BulkCreateAmsCheckpointForm {
  checkpoints: CreateAmsCheckpointForm[];
}

export interface AmsCheckpointFilter {
  search?: string;
  'checkpoint-type'?: 'asset' | 'project';
  'asset-id'?: string;
  'project-type-id'?: string;
  'execution-type'?: 'manual' | 'visual' | 'hearing';
  'applicable-audit-type-id'?: string;
  status?: 'active' | 'inactive' | 'all';
  page?: number;
  limit?: number;
  'sort-by'?: string;
  'sort-order'?: 'asc' | 'desc';
}

export interface AmsCheckpointListResponse {
  success: boolean;
  message: string;
  data: AmsCheckpoint[];
  pagination?: {
    page: number;
    limit: number;
    totalDocs: number;
    totalPages: number;
  };
}

export interface AmsCheckpointResponse {
  success: boolean;
  message: string;
  data: AmsCheckpoint;
}

export interface BulkAmsCheckpointResponse {
  success: boolean;
  message: string;
  data: {
    created: number;
    failed: number;
    total: number;
    failedDetails: Array<{
      index: number;
      error: string;
    }>;
  };
}

// ========== AMS Checkpoint API Services ==========

export const amsCheckpointService = {
  // Get all AMS checkpoints with filtering and pagination
  getAmsCheckpoints: async (filters: AmsCheckpointFilter): Promise<AmsCheckpointListResponse> => {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters['checkpoint-type']) params.append('checkpoint-type', filters['checkpoint-type']);
    if (filters['asset-id']) params.append('asset-id', filters['asset-id']);
    if (filters['project-type-id']) params.append('project-type-id', filters['project-type-id']);
    if (filters['execution-type']) params.append('execution-type', filters['execution-type']);
    if (filters['applicable-audit-type-id']) params.append('applicable-audit-type-id', filters['applicable-audit-type-id']);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters['sort-by']) params.append('sort-by', filters['sort-by']);
    if (filters['sort-order']) params.append('sort-order', filters['sort-order']);

    const response = await axiosInstance.get(`/ams-checkpoints?${params.toString()}`);
    return response.data;
  },

  // Get AMS checkpoint by ID
  getAmsCheckpointById: async (id: string): Promise<AmsCheckpointResponse> => {
    const response = await axiosInstance.get(`/ams-checkpoints/${id}`);
    return response.data;
  },

  // Create new AMS checkpoint
  createAmsCheckpoint: async (data: CreateAmsCheckpointForm): Promise<AmsCheckpointResponse> => {
    const response = await axiosInstance.post('/ams-checkpoints', data);
    return response.data;
  },

  // Create multiple AMS checkpoints in bulk
  createBulkAmsCheckpoints: async (data: BulkCreateAmsCheckpointForm): Promise<BulkAmsCheckpointResponse> => {
    const response = await axiosInstance.post('/ams-checkpoints/bulk', data);
    return response.data;
  },

  // Update AMS checkpoint
  updateAmsCheckpoint: async (id: string, data: UpdateAmsCheckpointForm): Promise<AmsCheckpointResponse> => {
    const response = await axiosInstance.put(`/ams-checkpoints/${id}`, data);
    return response.data;
  },

  // Inactivate AMS checkpoint (soft delete)
  inactivateAmsCheckpoint: async (id: string): Promise<AmsCheckpointResponse> => {
    const response = await axiosInstance.patch(`/ams-checkpoints/${id}/inactivate`);
    return response.data;
  },

  // Activate AMS checkpoint
  activateAmsCheckpoint: async (id: string): Promise<AmsCheckpointResponse> => {
    const response = await axiosInstance.patch(`/ams-checkpoints/${id}/activate`);
    return response.data;
  },

  // Bulk upload AMS checkpoints from Excel file. projectTypeId applies to Project-level
  // rows (which have no asset to derive the project type from).
  bulkUploadAmsCheckpoints: async (file: File, projectTypeId?: string): Promise<{
    success: boolean;
    message: string;
    data: {
      total: number;
      created: number;
      failed: number;
      errors: Array<{
        row: number;
        name: string;
        assetName: string;
        error: string;
        field?: string;
      }>;
    };
  }> => {
    const formData = new FormData();
    formData.append('file', file);
    if (projectTypeId) formData.append('projectTypeId', projectTypeId);

    const response = await axiosInstance.post('/ams-checkpoints/bulk-upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
